/**
 * TestingModePanel — platform-internal testing-mode override.
 *
 * Visible only to `user.platformInternal === true` users when Central
 * exposes testing mode for the session. Visibility is gated by the shell
 * adapter's `useTestingModeState` hook.
 *
 * Behavior (Pitchless-parity):
 *   - Fetches options + status from Central on open
 *   - Apply  → persists Central override (server-side) AND
 *              persists simulated central role + simulated BeHeard
 *              app-profile bundle in sessionStorage, then reloads
 *   - Clear  → removes server-side override AND clears both local
 *              overrides, then reloads
 *
 * Why two overrides:
 *   - Central's testing-mode API does NOT re-mint the simulating user's
 *     switch-context response → without the local mirrors, the sidebar /
 *     settings / role badge / useAppRole keep showing the tester's REAL
 *     role/scopes (see `auth/simulatedCentralRole.ts` for full rationale).
 *   - BeHeard's app-profile bundles (member_standard / _premium /
 *     _recruiter / _trainer / _org_admin) are app-specific — the Central
 *     panel doesn't model them, so we surface a multi-select here that
 *     ONLY shows when the simulated Central role is `member`.
 *
 * All API calls go via services/testingModeApi.ts (Bearer-only).
 */

import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { TestingModePanelProps } from '@levelup/ui-shell';
import type {
  ApplyOverrideParams,
  TestingModeOptions,
  TestingModeStatus,
} from '@levelup/central-auth';
import { selectCentralAccessToken } from '../store/slices/centralAuthSlice';
import {
  applyTestingModeOverride,
  clearTestingModeOverride,
  fetchTestingModeOptions,
  fetchTestingModeStatus,
} from '../services/testingModeApi';
import {
  getSimulatedCentralRole,
  setSimulatedCentralRole,
  clearSimulatedCentralRole,
  type SimulatedCentralRole,
} from '../auth/simulatedCentralRole';
import {
  getSimulatedAppProfile,
  setSimulatedAppProfile,
  clearSimulatedAppProfile,
} from '../auth/simulatedAppProfile';
import type { AppRoleKey } from '../../../shared/src/appRoles';

/**
 * BeHeard in-app roles — single-select dropdown matching the Central
 * scope bundles defined in shared/src/appRoles.ts. Each maps to exactly
 * one AppRoleKey whose scope bundle BootGate injects into workspace.scopes.
 */
/**
 * BeHeard in-app roles — matches the Final Role Model exactly.
 *
 * Each entry maps 1:1 to a real Central member role assignment.
 * Additive roles (premium, recruiter, trainer, org_admin) always
 * include Standard as the base — that's how Central assigns them.
 */
const APP_ROLE_CHOICES: { value: string; label: string; description: string; keys: AppRoleKey[] }[] = [
  {
    value: 'standard',
    label: 'Standard',
    description: 'Core product usage (practice, coaching, marketplace, portfolio).',
    keys: ['member_standard'],
  },
  {
    value: 'premium',
    label: 'Premium',
    description: 'Standard + AI features (coach, voice, TTS, listen).',
    keys: ['member_standard', 'member_premium'],
  },
  {
    value: 'recruiter',
    label: 'Recruiter',
    description: 'Recruiter + hiring pipelines only.',
    keys: ['member_recruiter'],
  },
  {
    value: 'trainer',
    label: 'Trainer',
    description: 'Training + assignments only.',
    keys: ['member_trainer'],
  },
  {
    value: 'org_admin',
    label: 'Org Admin',
    description: 'Org + member management only.',
    keys: ['member_org_admin'],
  },
];

const DEFAULT_APP_ROLE = APP_ROLE_CHOICES[0].value;

function appRoleFromKeys(keys: AppRoleKey[] | null): string {
  if (!keys || keys.length === 0) return DEFAULT_APP_ROLE;
  const sorted = [...keys].sort().join(',');
  const match = APP_ROLE_CHOICES.find((c) => [...c.keys].sort().join(',') === sorted);
  return match?.value ?? DEFAULT_APP_ROLE;
}

export function TestingModePanel({ isOpen, onClose }: TestingModePanelProps) {
  const token = useSelector(selectCentralAccessToken);

  const [options, setOptions] = useState<TestingModeOptions | null>(null);
  const [status, setStatus] = useState<TestingModeStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state — Central-side
  const [workspaceId, setWorkspaceId] = useState('');
  const [appId, setAppId] = useState('');
  const [role, setRole] = useState('');
  const [capabilityPreset, setCapabilityPreset] = useState('');
  const [visibilityPreset, setVisibilityPreset] = useState('');

  // Form state — BeHeard-side (single dropdown, only when role === 'member')
  const [appRole, setAppRolePreset] = useState<string>(
    () => appRoleFromKeys(getSimulatedAppProfile()),
  );

  useEffect(() => {
    if (!isOpen) return;
    if (!token) {
      setError('Not authenticated');
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    // Re-read overrides whenever the panel opens
    const persistedRole = getSimulatedCentralRole();
    const persistedProfile = getSimulatedAppProfile();

    Promise.all([fetchTestingModeOptions(token), fetchTestingModeStatus(token)])
      .then(([opts, stat]) => {
        if (cancelled) return;
        setOptions(opts);
        setStatus(stat);
        // Pre-fill from active override (server-side or local mirror)
        if (stat.override) {
          setWorkspaceId(stat.override.workspaceId ?? '');
          setAppId(stat.override.appId ?? '');
          setRole(persistedRole ?? stat.override.role ?? '');
          setCapabilityPreset(stat.override.capabilityPreset ?? '');
          setVisibilityPreset(stat.override.visibilityPreset ?? '');
        } else if (persistedRole) {
          setRole(persistedRole);
        }
        if (persistedProfile && persistedProfile.length > 0) {
          setAppRolePreset(appRoleFromKeys(persistedProfile));
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, token]);

  const handleApply = useCallback(async () => {
    if (!token) return;
    if (!workspaceId || !appId || !role) {
      setError('Workspace, App, and Role are required');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      // (1) Persist simulated Central role locally so BootGate can inject
      //     it into bootContext on reload — Central's testing-mode API
      //     does NOT re-mint the simulator's own switch-context response.
      const validRole =
        role === 'owner' || role === 'admin' || role === 'member' || role === 'viewer';
      if (validRole) {
        setSimulatedCentralRole(role as SimulatedCentralRole);
      } else {
        clearSimulatedCentralRole();
      }

      // (2) Persist BeHeard app-profile bundle ONLY when simulating member.
      //     For owner/admin/viewer the bundle is meaningless — clear stale state.
      const selectedChoice = APP_ROLE_CHOICES.find((c) => c.value === appRole);
      if (role === 'member' && selectedChoice) {
        setSimulatedAppProfile(selectedChoice.keys);
      } else {
        clearSimulatedAppProfile();
      }

      // (3) Server-side override (still required so backend sees the
      //     correct effective context for downstream API calls).
      const params: ApplyOverrideParams = {
        workspaceId,
        appId,
        role,
        capabilityPreset: capabilityPreset || undefined,
        visibilityPreset: visibilityPreset || undefined,
      };
      await applyTestingModeOverride(token, params);

      // (4) Reload so BootGate re-runs and injects the overrides.
      window.location.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Apply failed');
      setBusy(false);
    }
  }, [token, workspaceId, appId, role, capabilityPreset, visibilityPreset, appRole]);

  const handleClear = useCallback(async () => {
    if (!token) return;
    setBusy(true);
    setError(null);
    try {
      // Clear local mirrors first so a server-side failure still cleans
      // up the UI on next reload.
      clearSimulatedCentralRole();
      clearSimulatedAppProfile();
      await clearTestingModeOverride(token);
      window.location.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Clear failed');
      setBusy(false);
    }
  }, [token]);

  if (!isOpen) return null;

  // The shell's popover applies max-height ~480px with overflow:hidden.
  // Wrap in our own scroll container so Apply/Clear are always reachable
  // even with long option lists + the BeHeard app-role checkboxes.
  return (
    <div
      style={{
        maxHeight: '70vh',
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
      }}
    >
    <div className="p-4 space-y-4 max-w-md">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-gray-900">Testing Mode</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Platform-internal — preview the app as another role.
          </p>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-700" aria-label="Close">
          ✕
        </button>
      </div>

      {loading ? (
        <div className="py-8 flex justify-center">
          <div className="w-5 h-5 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : error ? (
        <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-2">{error}</div>
      ) : (
        <div className="space-y-3">
          {status?.active && (
            <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
              Testing mode is currently <strong>active</strong>.
            </div>
          )}

          <FieldSelect
            label="Workspace"
            value={workspaceId}
            onChange={setWorkspaceId}
            options={(options?.workspaces ?? []).map((w) => ({ value: w.id, label: w.name }))}
          />

          <FieldSelect
            label="App"
            value={appId}
            onChange={setAppId}
            options={(options?.apps ?? []).map((a) => ({ value: a.id, label: a.name }))}
          />

          <FieldSelect
            label="Central Role"
            value={role}
            onChange={setRole}
            options={(options?.roles ?? []).map((r) => ({ value: r.key, label: r.label }))}
          />

          <FieldSelect
            label="Capability preset"
            value={capabilityPreset}
            onChange={setCapabilityPreset}
            options={(options?.capabilityPresets ?? []).map((c) => ({ value: c.key, label: c.label }))}
            optional
          />

          <FieldSelect
            label="Visibility preset"
            value={visibilityPreset}
            onChange={setVisibilityPreset}
            options={(options?.visibilityPresets ?? []).map((v) => ({ value: v.key, label: v.label }))}
            optional
          />

          {/* BeHeard app-role dropdown — only when simulating member */}
          {role === 'member' && (
            <div className="border-t border-gray-100 pt-3">
              <FieldSelect
                label="BeHeard App Role (member only)"
                value={appRole}
                onChange={setAppRolePreset}
                options={APP_ROLE_CHOICES.map((p) => ({ value: p.value, label: p.label }))}
              />
              {(() => {
                const desc = APP_ROLE_CHOICES.find((p) => p.value === appRole)?.description;
                return desc ? (
                  <p className="text-[11px] text-gray-500 mt-1 leading-snug">{desc}</p>
                ) : null;
              })()}
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <button
              onClick={handleApply}
              disabled={busy}
              className="flex-1 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              Apply
            </button>
            <button
              onClick={handleClear}
              disabled={busy || (!status?.active && !getSimulatedCentralRole() && !getSimulatedAppProfile())}
              className="flex-1 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              Clear
            </button>
          </div>
        </div>
      )}
    </div>
    </div>
  );
}

function FieldSelect({
  label,
  value,
  onChange,
  options,
  optional,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
  optional?: boolean;
}) {
  return (
    <label className="block">
      <span className="text-xs text-gray-600 uppercase tracking-wide">
        {label}
        {optional && <span className="ml-1 text-gray-400">(optional)</span>}
      </span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-md border-gray-300 text-sm focus:border-primary-500 focus:ring-primary-500"
      >
        <option value="">— select —</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}
